import { useEffect, useId, useRef, useState } from 'react'
import { Lock } from 'lucide-react'
import {
  M2C_DECREASE_RESOLUTIONS, M2C_RECONCILE_STATES, M2C_RESTORABLE_STATES, M2C_RESTORE_RESOLUTIONS, m2cUnresolved, m2cQueryReady, m2cHasLatchProof, isM2cPreDispatchRefusal, useM2cPermitSafety, useM2cFullHistory, useM2cPreview, useM2cReconcile, useM2cRecheckHistory, useM2cRestore, useM2cViewer,
  type M2cActionResult, type M2cReconcileResolution, type M2cWriteOperation,
} from '../../api/m2c'
import { IS_DEMO_MODE } from '../../lib/demoMode'
import Card from '../Card'
import SectionHead from '../SectionHead'
import Pagination from '../Pagination'
import { Badge } from '../StatusBadge'
import { M2cConfirmation } from './M2cConfirmation'
import { READ_ONLY_HINT, berlinDate, berlinTime, euro, fieldClass, outcomeText, primaryButton, readbackText, secondaryButton, stateLabel } from './M2cActionHistory'

type ResolutionText = { label: string; hint: string }
const OPERATOR_ASSERTED: ResolutionText = { label: 'Operator-Angabe (nicht systemgeprüft)', hint: 'Wird nur als Operator-Angabe gespeichert, nie als Readback bestätigt und löst keine Sperre.' }
const DECREASE_TEXT: Record<M2cReconcileResolution, ResolutionText> = {
  readback: { label: 'Erneut lesen (nur Lesen)', hint: 'Der Server liest Amazon erneut. Beim Ausgangsgebot sind zwei stabile Lesungen nach der Wartezeit nötig.' },
  accept_not_applied: { label: 'Nicht angewendet bestätigen', hint: 'Nur wenn Amazon nach der Wartezeit stabil das Ausgangsgebot zeigt. Ein neuer Versuch braucht eine neue Berechnung.' },
  close_superseded: { label: 'Als überholt schließen', hint: 'Drittes Gebot oder externe Änderung: Aktion bleibt dokumentiert, nichts wird überschrieben.' },
  operator_asserted: OPERATOR_ASSERTED,
}
// Restore (T6a): readback must show the baseline after the restore was authorized;
// "not applied" needs two stable reads of the unchanged decreased bid after the wait.
const RESTORE_TEXT: Record<M2cReconcileResolution, ResolutionText> = {
  readback: { label: 'Erneut lesen (nur Lesen)', hint: 'Der Server liest Amazon erneut. Nur das Ausgangsgebot nach der Restore-Freigabe bestätigt die Wiederherstellung; die Freigabe ist dann verbraucht.' },
  accept_not_applied: { label: 'Wiederherstellung nicht angewendet bestätigen', hint: 'Nur wenn Amazon nach der Wartezeit zweimal stabil das gesenkte Gebot zeigt. Die Aktion wird blockiert; nichts wird gesendet.' },
  close_superseded: DECREASE_TEXT.close_superseded,
  operator_asserted: OPERATOR_ASSERTED,
}
/** Exactly the resolutions the server accepts for this direction (reconcile.ts). */
function resolutionsFor(restore: boolean): Array<{ value: M2cReconcileResolution } & ResolutionText> {
  return (restore ? M2C_RESTORE_RESOLUTIONS : M2C_DECREASE_RESOLUTIONS).map(value => ({ value, ...(restore ? RESTORE_TEXT : DECREASE_TEXT)[value] }))
}
const ASSERTED_NOTICE = 'Als Operator-Angabe gespeichert — nicht systemgeprüft. Sie gilt nie als Readback-Nachweis und löst keine Sperre.'

function refusalText(err: unknown, operation: Extract<M2cWriteOperation, 'reconcile' | 'restore'>): string | null {
  if (!isM2cPreDispatchRefusal(err, operation)) return null
  switch (err.status) {
    case 400: return `Anfrage abgelehnt (400): ${err.message}.`
    case 403: return `Keine Berechtigung (403): ${err.message}.`
    case 404: return `Nicht gefunden (404): ${err.message}.`
    case 409: return `Daten wurden zwischenzeitlich geändert (409): ${err.message}. Neu laden und erneut prüfen.`
    case 423: return 'Read-only-Modus (423): Änderungen an Amazon sind gesperrt.'
    case 503: return `Voraussetzungen nicht erfüllt (503): ${err.message}.`
    default: return null
  }
}

const RESTORE_STATES: ReadonlySet<string> = new Set(['restore_requested', 'restoring', 'restore_unresolved'])
const STOP_HINT = '„Pilot stoppen“ im Pilot-Abschnitt bleibt jederzeit verfügbar.'

/** What the server allows for this row: reconcile_required (decrease) and restore_unresolved
 * (restore, T6a) have a reconcile route; every other state has none. */
function serverPath(result: M2cActionResult, restoreLatched: boolean): string | null {
  if (M2C_RECONCILE_STATES.has(result.state)) return null
  if (RESTORE_STATES.has(result.state) || restoreLatched) {
    return `Manuelle Klärung nötig: Der Server klärt eine Wiederherstellung nur im Zustand „${stateLabel('restore_unresolved')}“, in diesem Zustand nicht (M2C_RECONCILE_RESTORE_UNSUPPORTED). Ergebnis bleibt ungeklärt; keine erneute Wiederherstellung senden, Amazon-Gebot manuell prüfen. ${STOP_HINT}`
  }
  if (m2cUnresolved(result)) {
    return `Für den Zustand „${stateLabel(result.state)}“ bietet der Server keine Klärung an. Verlauf später erneut prüfen; Ergebnis bleibt bis dahin ungeklärt. ${STOP_HINT}`
  }
  return `Der Server bietet für den Zustand „${stateLabel(result.state)}“ keine Klärung an. Vollständigen Verlauf erneut prüfen; reicht der Servernachweis nicht, bleibt die Sperre in dieser Sitzung bestehen. ${STOP_HINT}`
}

type ReconcileOutcome = { result: M2cActionResult; resolution: M2cReconcileResolution }
/** Result of the GET-only full-history recheck, kept on panel level: a successful recheck
 * removes its row, the notice must stay until closed or replaced by the next recheck. */
type RecheckNotice = { kind: 'confirmed' | 'open' | 'error'; actionId: string; at: string; text: string }
const NOTICE_BADGE: Record<RecheckNotice['kind'], { tone: 'good' | 'warn' | 'bad'; label: string }> = {
  confirmed: { tone: 'good', label: 'Geklärt' }, open: { tone: 'warn', label: 'Weiter offen' }, error: { tone: 'bad', label: 'Fehler' },
}
type ReconcileItemProps = {
  result: M2cActionResult; onResult: (outcome: ReconcileOutcome) => void; blocker: { text: string; lock?: boolean } | null
  onRecheck: (actionId: string) => void; recheckPending: boolean
}
function ReconcileItem({ result, onResult, blocker, onRecheck, recheckPending }: ReconcileItemProps) {
  const id = useId()
  const mutation = useM2cReconcile(result.action.permitId)
  const safety = useM2cPermitSafety(result.action.permitId)
  const [resolution, setResolution] = useState<M2cReconcileResolution | ''>('')
  const [note, setNote] = useState('')
  const [errors, setErrors] = useState({ resolution: '', note: '' })
  const [message, setMessage] = useState('')
  const lock = useRef(false), alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  const action = result.action
  const resolutions = resolutionsFor(result.state === 'restore_unresolved')
  const latched = safety.actionIds.includes(action.id)
  const operation = safety.operations[action.id]
  const operationLabel = operation?.operation === 'restore' ? 'Wiederherstellung' : 'Senkung'
  const recorded = safety.reconciled.includes(action.id)
  const proven = m2cHasLatchProof(result, operation)
  const path = serverPath(result, latched && operation?.operation === 'restore' && !proven)
  const remote = result.readEvidence?.status === 'found'
    ? `${euro(result.readEvidence.bidCents)} (${berlinTime(result.readEvidence.observedAt)})` : readbackText(result.readEvidence)

  async function submit() {
    const next = { resolution: resolution ? '' : 'Klärung wählen.', note: note.trim() ? '' : 'Notiz erforderlich.' }
    setErrors(next)
    if (next.resolution || next.note || !resolution || blocker || path || lock.current) return
    lock.current = true
    try {
      const response = await mutation.mutateAsync({ actionId: action.id, expectedRevision: action.revision, note: note.trim(), resolution })
      if (alive.current) { setMessage(''); setNote(''); setResolution('') }
      onResult({ result: response, resolution })
    } catch (err) {
      if (alive.current) setMessage(refusalText(err, 'reconcile') ?? `Klärung nicht bestätigt: ${err instanceof Error ? err.message : 'unbekannter Fehler'}. Verlauf prüfen, nicht blind wiederholen.`)
    } finally { lock.current = false }
  }

  return <div role="group" aria-label={`Klärung für ${action.id}`} className="space-y-3 rounded-lg border border-[var(--border)] p-4">
    <p className="font-medium"><span className="font-mono text-xs">{action.id}</span>{` · ${stateLabel(result.state)}`}</p>
    {latched && <div className="space-y-2">
      <Badge tone="warn">Klärung offen</Badge>
      <p>{`${operationLabel}: Ergebnis seit ${operation ? berlinTime(new Date(operation.requestedAt).toISOString()) : 'unbekanntem Zeitpunkt'} ungeklärt.`}</p>
      {!proven && <p className="text-xs text-[var(--muted-foreground)]">
        {result.state === 'cancelled' || result.outcome?.status === 'rejected_pre_dispatch'
          ? 'Servernachweis für den letzten ungeklärten Vorgang erforderlich: Ein Abbruch oder eine Ablehnung allein beweist nicht, dass dieser Vorgang nie gesendet oder sicher abgeschlossen wurde.'
          : `Servernachweis für den letzten ungeklärten Vorgang erforderlich: Identität, aktueller Lesezeitpunkt, eine neuere Revision als beim Senden und ${operation?.operation === 'restore' ? 'Ausgangsgebot mit bestätigter Wiederherstellung oder eine in dieser Sitzung vom Server geprüfte Klärung „nicht angewendet“' : 'exaktes Zielgebot oder ein vom Server geprüfter Abschluss'} müssen passen. Ein älterer Readback, eine höhere Revision oder eine Operator-Angabe allein genügt nicht.`}
      </p>}
      {recorded && <p className="text-xs text-[var(--muted-foreground)]">Systemprüfung gespeichert. Falls der Verlauf später nachgezogen ist, hier erneut vollständig prüfen.</p>}
      <button className={secondaryButton} disabled={safety.busy || recheckPending} onClick={() => onRecheck(action.id)}>
        {recheckPending ? 'Verlauf wird geprüft…' : 'Vollständigen Verlauf erneut prüfen'}
      </button>
      <p className="text-xs text-[var(--muted-foreground)]">Nur Lesen: lädt den vollständigen Verlauf neu, reicht keine Änderung ein.</p>
    </div>}
    <p className="tabular-nums">{`Gebot alt ${euro(action.oldBidCents)} · Ziel ${euro(action.targetBidCents)} · Amazon zuletzt ${remote}`}</p>
    <p>{`Ergebnis: ${outcomeText(result.outcome)} · Keyword ${action.entity.entityAmazonId} · Erste Sendung ${action.firstDispatchedAt ? berlinTime(action.firstDispatchedAt) : '—'}`}</p>
    {path ? <p className="font-medium">{path}</p> : <>
    {blocker && <p className="flex items-center gap-1 text-xs text-[var(--muted-foreground)]">{blocker.lock && <Lock className="size-4" aria-hidden="true" />}{blocker.text}</p>}
    <fieldset className="space-y-2" aria-describedby={errors.resolution ? `${id}-resolution-error` : undefined}>
      <legend className="font-medium">Klärung</legend>
      {resolutions.map(option => <label key={option.value} className="flex items-start gap-2">
        <input type="radio" className="mt-1 size-4 focus-visible:ring-2 focus-visible:ring-[var(--ring)]" name={`${id}-resolution`} value={option.value}
          checked={resolution === option.value} onChange={() => { setResolution(option.value); setErrors(e => ({ ...e, resolution: '' })) }} />
        <span><span className="block">{option.label}</span><span className="block text-xs text-[var(--muted-foreground)]">{option.hint}</span></span>
      </label>)}
      {errors.resolution && <p id={`${id}-resolution-error`} className="text-xs text-[var(--destructive)]">{errors.resolution}</p>}
    </fieldset>
    <div className="space-y-1">
      <label className="block font-medium" htmlFor={`${id}-note`}>Notiz (Pflicht)</label>
      <textarea id={`${id}-note`} className={fieldClass} rows={2} value={note} aria-invalid={!!errors.note}
        aria-describedby={errors.note ? `${id}-note-error` : undefined}
        onBlur={() => setErrors(e => ({ ...e, note: note.trim() ? '' : 'Notiz erforderlich.' }))}
        onChange={e => { setNote(e.target.value); setErrors(x => ({ ...x, note: '' })) }} />
      {errors.note && <p id={`${id}-note-error`} className="text-xs text-[var(--destructive)]">{errors.note}</p>}
    </div>
    <button className={secondaryButton} disabled={mutation.isPending || safety.busy || !!blocker} onClick={() => void submit()}>{mutation.isPending ? 'Klärung wird eingereicht…' : 'Klärung einreichen'}</button>
    </>}
    {message && <p role="alert" className="text-[var(--destructive)]">{message}</p>}
  </div>
}

type RestoreMessage = { kind: 'alert' | 'status'; text: string }

function RestoreSection({ permitId, revision, actions, historyComplete }: { permitId: string; revision: number; actions: M2cActionResult[]; historyComplete: boolean }) {
  const id = useId()
  const preview = useM2cPreview(permitId, revision)
  const viewer = useM2cViewer()
  const restore = useM2cRestore(permitId)
  const [actionId, setActionId] = useState('')
  const [approvalId, setApprovalId] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [message, setMessage] = useState<RestoreMessage | null>(null)
  const safety = useM2cPermitSafety(permitId)
  const lock = useRef(false), alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])

  const restorable = actions.filter(r => M2C_RESTORABLE_STATES.has(r.state) && r.action.firstDispatchedAt !== null)
  const chosen = restorable.find(r => r.action.id === actionId) ?? null
  const permit = preview.data?.permit.id === permitId ? preview.data.permit : null
  const blockers: Array<{ text: string; lock?: boolean }> = []
  if (IS_DEMO_MODE) blockers.push({ text: READ_ONLY_HINT, lock: true })
  if (!permit) blockers.push({ text: 'Permit nicht geladen.' })
  else if (viewer.isPending) blockers.push({ text: 'Benutzer wird geprüft.' })
  else if (viewer.error || !viewer.data || viewer.data.id !== permit.operatorId) {
    blockers.push({ text: `Wiederherstellung nur durch den im Permit eingetragenen Operator (Benutzer-ID ${permit.operatorId}).` })
  }
  if (!historyComplete) blockers.push({ text: 'Verlauf nicht vollständig geladen.' })
  if (!m2cQueryReady(preview)) blockers.push({ text: 'Vorschau nicht aktuell bestätigt — erneut laden.' })
  const unresolved = actions.filter(m2cUnresolved)
  if (unresolved[0]) blockers.push({ text: `Ungeklärte Aktion ${unresolved[0].action.id} blockiert weitere Änderungen. Insgesamt ${unresolved.length} ungeklärte ${unresolved.length === 1 ? 'Aktion' : 'Aktionen'}.` })
  if (!chosen) blockers.push({ text: 'Keine Aktion gewählt.' })
  else if (!chosen.action.recoveryExpiresAt) blockers.push({ text: 'Keine Recovery-Frist gespeichert.' })
  else if (Date.now() >= Date.parse(chosen.action.recoveryExpiresAt)) blockers.push({ text: `Recovery-Frist abgelaufen am ${berlinDate(chosen.action.recoveryExpiresAt)}.` })
  if (!approvalId.trim()) blockers.push({ text: 'Freigabe-ID des Eigentümers fehlt.' })
  if (safety.latched) blockers.push({ text: 'Ergebnis einer Permit-Aktion unbekannt — nicht erneut senden.' })
  const canRestore = blockers.length === 0 && !restore.isPending && !safety.busy

  async function confirm() {
    if (!chosen || !canRestore || lock.current) return
    lock.current = true
    try {
      const result = await restore.mutateAsync({ actionId: chosen.action.id, expectedRevision: chosen.action.revision, ownerApprovalId: approvalId.trim() })
      if (!alive.current) return
      if (result.state === 'restored') setMessage({ kind: 'status', text: 'Ausgangsgebot wiederhergestellt und per Readback bestätigt.' })
      else if (result.state === 'restore_unresolved' || result.outcome?.status === 'unknown' || result.outcome?.status === 'rejected') {
        setMessage({ kind: 'alert', text: `Wiederherstellung ungeklärt — keine automatische Wiederholung; Klärung erforderlich. ${outcomeText(result.outcome)}` })
      } else setMessage({ kind: 'status', text: `Server-Ergebnis: ${stateLabel(result.state)}.` })
    } catch (err) {
      if (!alive.current) return
      const refusal = refusalText(err, 'restore')
      if (refusal) setMessage({ kind: 'alert', text: refusal })
      else {
        setMessage({ kind: 'alert', text: 'Ergebnis der Wiederherstellung unbekannt — nicht erneut senden; Verlauf und Klärung prüfen.' })
      }
    } finally {
      lock.current = false
      if (alive.current) { setConfirming(false); setApprovalId('') }
    }
  }

  return <div role="group" aria-label="Wiederherstellung des Ausgangsgebots" className="space-y-3 border-t border-[var(--border)] pt-4">
    <SectionHead level={4} title="Wiederherstellung des Ausgangsgebots" />
    <p>Setzt genau das gespeicherte Ausgangsgebot einer Aktion (eine Erhöhung gegenüber der Senkung). Nur mit gesonderter Freigabe des Eigentümers für genau diese Aktion; die Freigabe-ID allein berechtigt nichts — der Server prüft Freigabe, Frist und aktuelles Gebot. Zählt zur Tagesquote.</p>
    {restorable.length === 0
      ? <p className="text-[var(--muted-foreground)]">Keine Aktion mit bestätigtem Readback zur Wiederherstellung.</p>
      : <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1"><label className="block font-medium" htmlFor={`${id}-action`}>Aktion für Wiederherstellung</label>
          <select id={`${id}-action`} className={fieldClass} value={actionId} onChange={e => { setActionId(e.target.value); setConfirming(false) }}>
            <option value="">Aktion wählen</option>
            {restorable.map(r => <option key={r.action.id} value={r.action.id}>
              {`${r.action.id} · ${euro(r.action.targetBidCents)} → ${euro(r.action.oldBidCents)}${r.action.recoveryExpiresAt ? ` · bis ${berlinDate(r.action.recoveryExpiresAt)}` : ''}`}</option>)}
          </select></div>
        <div className="space-y-1"><label className="block font-medium" htmlFor={`${id}-approval`}>Freigabe-ID des Eigentümers</label>
          <input id={`${id}-approval`} className={fieldClass} value={approvalId} onChange={e => setApprovalId(e.target.value)} /></div>
      </div>}
    <ul aria-label="Wiederherstellung gesperrt" className="space-y-1 text-xs text-[var(--muted-foreground)]">
      {blockers.map(b => <li key={b.text} className="flex items-center gap-1">{b.lock && <Lock className="size-4" aria-hidden="true" />}{b.text}</li>)}
    </ul>
    <button className={secondaryButton} disabled={!canRestore} onClick={() => setConfirming(true)}>Wiederherstellung anfragen</button>
    {confirming && chosen && <M2cConfirmation label="Wiederherstellung bestätigen" onClose={() => setConfirming(false)}>
      <p>{`Gebot von Keyword ${chosen.action.entity.entityAmazonId} wird exakt auf den Ausgangswert ${euro(chosen.action.oldBidCents)} gesetzt (aktuell laut Journal ${euro(chosen.action.targetBidCents)}). Fortfahren?`}</p>
      <div className="flex flex-wrap gap-2">
        <button className={primaryButton} disabled={restore.isPending} onClick={() => void confirm()}>Bestätigen</button>
        <button data-m2c-cancel className={secondaryButton} disabled={restore.isPending} onClick={() => setConfirming(false)}>Abbrechen</button>
      </div>
    </M2cConfirmation>}
    {message && <p role={message.kind} className={message.kind === 'alert' ? 'text-[var(--destructive)]' : ''}>{message.text}</p>}
  </div>
}

export function M2cReconcilePanel({ permitId, revision }: { permitId: string; revision: number }) {
  const history = useM2cFullHistory(permitId)
  const safety = useM2cPermitSafety(permitId)
  const preview = useM2cPreview(permitId, revision)
  const viewer = useM2cViewer()
  const recheck = useM2cRecheckHistory(permitId)
  const [lastOutcome, setLastOutcome] = useState<ReconcileOutcome | null>(null)
  const [notice, setNotice] = useState<RecheckNotice | null>(null)
  const lastResult = lastOutcome?.result ?? null
  const [page, setPage] = useState(0)
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])

  async function recheckHistory(actionId: string) {
    try {
      const released = await recheck.mutateAsync()
      if (alive.current) setNotice({ kind: released ? 'confirmed' : 'open', actionId, at: new Date().toISOString(), text: released
        ? 'Klärung anhand des vollständigen Verlaufs bestätigt.'
        : 'Klärung offen: Der vollständige Verlauf liefert noch keinen ausreichenden Servernachweis für alle ungeklärten Vorgänge.' })
    } catch (err) {
      if (alive.current) setNotice({ kind: 'error', actionId, at: new Date().toISOString(),
        text: `Verlauf nicht bestätigt: ${err instanceof Error ? err.message : 'unbekannter Fehler'}. Erneut prüfen.` })
    }
  }
  const actions = history.data?.actions ?? []
  // Every server-unresolved or locally latched action is listed with its server path.
  const queue = actions.filter(r => M2C_RECONCILE_STATES.has(r.state) || m2cUnresolved(r) || safety.actionIds.includes(r.action.id))
  // O2: only the permit operator; admin or "*" is no bypass. The server decides (403/423).
  const permit = preview.data?.permit.id === permitId ? preview.data.permit : null
  const reconcileBlocker = IS_DEMO_MODE ? { text: READ_ONLY_HINT, lock: true }
    : permit && viewer.isSuccess && viewer.data?.id !== permit.operatorId
      ? { text: `Klärung nur durch den im Permit eingetragenen Operator (Benutzer-ID ${permit.operatorId}); admin oder * genügen nicht.` } : null
  const missing = safety.actionIds.filter(id => !actions.some(r => r.action.id === id))
  const visiblePage = Math.min(page, Math.max(0, Math.ceil(queue.length / 25) - 1))
  return <Card className="rounded-lg p-4 space-y-4 text-sm" role="region" aria-label="M2c-Klärung">
    <SectionHead level={3} title="Klärung und Wiederherstellung" subtitle="Ungeklärte Aktionen blockieren den gesamten Pilot. Keine automatische Wiederholung; der Server prüft jede Klärung." />
    {history.isPending && <div role="status" aria-label="Klärungsliste wird geladen" className="h-16 animate-pulse rounded-lg bg-[var(--muted)]" />}
    {history.error && <div role="alert" className="space-y-2 text-[var(--destructive)]"><p>{history.error.message}</p>
      <button className={secondaryButton} onClick={() => void history.refetch()}>Klärungsliste erneut laden</button></div>}
    {notice && <div role={notice.kind === 'error' ? 'alert' : 'status'} aria-label="Ergebnis der Verlaufsprüfung"
      className="space-y-2 rounded-lg border border-[var(--border)] p-4">
      <p className="flex flex-wrap items-center gap-2">
        <Badge tone={NOTICE_BADGE[notice.kind].tone}>{NOTICE_BADGE[notice.kind].label}</Badge>
        <span className="font-mono text-xs">{notice.actionId}</span>
        <span className="text-xs text-[var(--muted-foreground)]">{`geprüft ${berlinTime(notice.at)}`}</span>
      </p>
      <p className={notice.kind === 'error' ? 'text-[var(--destructive)]' : ''}>{notice.text}</p>
      <button className={secondaryButton} onClick={() => setNotice(null)}>Schließen</button>
    </div>}
    {missing.length > 0 && <div className="space-y-2" role="status">
      <Badge tone="warn">Klärung offen</Badge>
      <details open><summary>{`${missing.length} ungeklärte ${missing.length === 1 ? 'Aktion fehlt' : 'Aktionen fehlen'} im geladenen Verlauf`}</summary>
        {missing.map(id => <p key={id}><span className="font-mono text-xs">{id}</span> fehlt im geladenen Verlauf. Servernachweis für den letzten ungeklärten Vorgang erforderlich.</p>)}
      </details>
      {!history.error && <button className={secondaryButton} onClick={() => void history.refetch()}>Klärungsliste erneut laden</button>}
    </div>}
    {history.data && <>
      {!history.data.complete && <p><span className="font-medium">Teilweise:</span> Daten unvollständig: Verlauf nicht vollständig geladen, weitere ungeklärte Aktionen möglich.</p>}
      {queue.length === 0 && missing.length === 0 ? <p className="text-[var(--muted-foreground)]">Keine ungeklärten Aktionen.</p>
        : queue.slice(visiblePage * 25, (visiblePage + 1) * 25).map(r => <ReconcileItem key={`${r.action.id}/${r.action.revision}`} result={r} onResult={setLastOutcome} blocker={reconcileBlocker}
          onRecheck={actionId => void recheckHistory(actionId)} recheckPending={recheck.isPending} />)}
      {queue.length > 25 && <Pagination label="Klärung" page={visiblePage} pageSize={25} totalItems={queue.length} pageSizeOptions={[25]} onPageChange={setPage} onPageSizeChange={() => {}} />}
    </>}
    {lastResult && <p role="status" title={lastResult.action.id}>{`Server-Ergebnis: ${stateLabel(lastResult.state)}`}</p>}
    {lastOutcome?.resolution === 'operator_asserted' && <p role="status" className="text-xs text-[var(--muted-foreground)]">{`${lastOutcome.result.action.id}: ${ASSERTED_NOTICE}`}</p>}
    <RestoreSection permitId={permitId} revision={revision} actions={actions} historyComplete={m2cQueryReady(history) && !!history.data?.complete} />
  </Card>
}

export default M2cReconcilePanel
